/**
 * Composable for gallery import: pick folder or files.
 * If metadata.json exists with valid export structure, create gallery and pair images by filename.
 * Otherwise import images only (add to current gallery or create "Imported" gallery).
 */

import { ref } from 'vue'
import { apiClient } from '@/services/api'

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|heic|bmp|tiff?)$/i
const METADATA_NAME = 'metadata.json'

export interface ImportMetadata {
  exportVersion?: number
  name: string
  description?: string | null
  galleryId?: string
  exportedAt?: string
  images: Array<{ filename: string; id: string; [key: string]: unknown }>
}

function isImageFile(name: string, file: File): boolean {
  return IMAGE_EXTENSIONS.test(name) || file.type.startsWith('image/')
}

function parseMetadata(file: File): Promise<ImportMetadata | null> {
  return file.text().then((text) => {
    try {
      const data = JSON.parse(text) as ImportMetadata
      return data
    } catch {
      return null
    }
  })
}

function hasValidExportStructure(metadata: ImportMetadata): boolean {
  return (
    typeof metadata.exportVersion === 'number' &&
    Array.isArray(metadata.images) &&
    metadata.images.length >= 0 &&
    typeof metadata.name === 'string'
  )
}

export function useGalleryImport() {
  const isImporting = ref(false)
  const importError = ref<string | null>(null)
  const importProgress = ref({ current: 0, total: 0, phase: '' })

  async function importFromFolder(
    brandId?: string,
    getFiles?: () => Promise<Array<{ file: File; name: string }>>,
    currentGalleryId?: string | null
  ): Promise<{ galleryId: string; name: string } | null> {
    isImporting.value = true
    importError.value = null
    importProgress.value = { current: 0, total: 0, phase: 'Selecting...' }

    try {
      let files: Array<{ file: File; name: string }>
      if (getFiles) {
        files = await getFiles()
      } else if ('showDirectoryPicker' in window) {
        try {
          files = await pickFolderWithFileSystemAPI()
        } catch (e) {
          if ((e as Error).name === 'AbortError') {
            return null
          }
          throw e
        }
      } else {
        importError.value = 'Pick a folder or use file picker to select images (and optional metadata.json).'
        return null
      }

      const metadataEntry = files.find(
        (f) => f.name === METADATA_NAME || f.name.toLowerCase() === METADATA_NAME
      )
      let metadata: ImportMetadata | null = null
      if (metadataEntry) {
        metadata = await parseMetadata(metadataEntry.file)
        if (metadata && !hasValidExportStructure(metadata)) {
          metadata = null
        }
      }

      if (metadata) {
        return await importWithMetadata(metadata, files, brandId)
      }

      return await importImagesOnly(files, brandId, currentGalleryId)
    } catch (err: unknown) {
      importError.value = err instanceof Error ? err.message : String(err)
      return null
    } finally {
      isImporting.value = false
      importProgress.value = { current: 0, total: 0, phase: '' }
    }
  }

  async function importWithMetadata(
    metadata: ImportMetadata,
    files: Array<{ file: File; name: string }>,
    brandId?: string
  ): Promise<{ galleryId: string; name: string } | null> {
    const fileMap = new Map<string, File>()
    for (const f of files) {
      if (f.name.toLowerCase() !== METADATA_NAME) {
        fileMap.set(f.name, f.file)
      }
    }

    importProgress.value = { current: 0, total: 1, phase: 'Creating gallery...' }
    const gallery = await apiClient.createGallery({
      name: metadata.name,
      description: metadata.description ?? undefined,
      ownerBrandId: brandId,
      isPrivate: true,
    })

    const total = metadata.images.length
    const imageIds: string[] = []
    for (let i = 0; i < metadata.images.length; i++) {
      const img = metadata.images[i]
      importProgress.value = {
        current: i,
        total,
        phase: `Uploading ${img.filename || img.id}...`,
      }
      const file = fileMap.get(img.filename)
      if (!file) {
        console.warn(`Import: file not found for ${img.filename}, skipping`)
        continue
      }
      try {
        const uploadResult = await apiClient.uploadImage(file, brandId)
        if (uploadResult?.id) {
          imageIds.push(uploadResult.id)
        }
      } catch (err) {
        console.warn(`Import: upload failed for ${img.filename}`, err)
      }
    }

    if (imageIds.length > 0) {
      importProgress.value = { current: total, total, phase: 'Adding to gallery...' }
      await apiClient.addImagesToGallery(gallery.id, imageIds)
    }

    return { galleryId: gallery.id, name: gallery.name }
  }

  async function importImagesOnly(
    files: Array<{ file: File; name: string }>,
    brandId?: string,
    currentGalleryId?: string | null
  ): Promise<{ galleryId: string; name: string } | null> {
    const imageFiles = files.filter((f) => isImageFile(f.name, f.file))
    if (imageFiles.length === 0) {
      importError.value = 'No image files found'
      return null
    }

    const imageIds: string[] = []
    for (let i = 0; i < imageFiles.length; i++) {
      const { file, name } = imageFiles[i]
      importProgress.value = {
        current: i,
        total: imageFiles.length,
        phase: `Uploading ${name}...`,
      }
      try {
        const uploadResult = await apiClient.uploadImage(file, brandId)
        if (uploadResult?.id) {
          imageIds.push(uploadResult.id)
        }
      } catch (err) {
        console.warn(`Import: upload failed for ${name}`, err)
      }
    }

    if (imageIds.length === 0) {
      importError.value = 'No images could be uploaded'
      return null
    }

    if (!currentGalleryId) {
      importError.value = 'Select a gallery first, then import images.'
      return null
    }

    importProgress.value = {
      current: imageFiles.length,
      total: imageFiles.length,
      phase: 'Adding to gallery...',
    }
    await apiClient.addImagesToGallery(currentGalleryId, imageIds)
    const gallery = await apiClient.fetchGallery(currentGalleryId, brandId)
    return { galleryId: gallery.id, name: gallery.name }
  }

  return {
    isImporting,
    importError,
    importProgress,
    importFromFolder,
  }
}

async function pickFolderWithFileSystemAPI(): Promise<Array<{ file: File; name: string }>> {
  type DirHandle = { entries: () => AsyncIterableIterator<[string, FileSystemFileHandle | FileSystemDirectoryHandle]> }
  const dirHandle = await (window as unknown as { showDirectoryPicker: () => Promise<DirHandle> }).showDirectoryPicker()
  const files: Array<{ file: File; name: string }> = []
  for await (const [name, handle] of dirHandle.entries()) {
    if (handle.kind === 'file') {
      const file = await (handle as FileSystemFileHandle).getFile()
      files.push({ file, name })
    }
  }
  return files
}
